// components/clients/ClientModal.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import { Business, AccessTime, AttachMoney, Category, Receipt } from '@mui/icons-material';
import { IVA_OPTIONS, TIPO_OPTIONS } from './parametros';

const ClientModal = ({ 
  open, 
  onClose, 
  onSave, 
  client = null, 
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    razon_social: '',
    horas: '',
    monto_base: '',
    tipo: '',
    iva: ''
  });
  const [errors, setErrors] = useState({});

  
  const tipoOptions = TIPO_OPTIONS
  const ivaOptions = IVA_OPTIONS

  // Resetear formulario cuando se abre/cierra el modal
  useEffect(() => {
    if (open) {
      if (client) {
        console.log('Cargando datos del cliente en el formulario:', client);
        // Modo edición - cargar datos del cliente
        setFormData({
          razon_social: client.razon_social || '',
          horas: client.horas?.toString() || '',
          monto_base: client.monto_base?.toString() || '',
          tipo: client.tipo !== null && client.tipo !== undefined ? client.tipo.toString() : '',
          iva: client.iva !== null && client.iva !== undefined ? client.iva.toString() : ''
        });
      } else {
        // Modo creación - limpiar formulario
        setFormData({
          razon_social: '',
          horas: '',
          monto_base: '',
          tipo: '',
          iva: ''
        });
      }
      setErrors({});
    }
  }, [open, client]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.razon_social.trim()) {
      newErrors.razon_social = 'La razón social es requerida';
    }

    if (!formData.horas) {
      newErrors.horas = 'La cantidad de horas es requerida';
    } else if (parseFloat(formData.horas) <= 0) {
      newErrors.horas = 'Las horas deben ser mayor a 0';
    }

    if (!formData.monto_base) {
      newErrors.monto_base = 'El monto base es requerido';
    } else if (parseFloat(formData.monto_base) <= 0) {
      newErrors.monto_base = 'El monto base debe ser mayor a 0';
    }

    if (formData.tipo === '' || formData.tipo === null || formData.tipo === undefined) {
      newErrors.tipo = 'El tipo de cliente es requerido';
    }

    if (formData.iva === '' || formData.iva === null || formData.iva === undefined) {
      newErrors.iva = 'La configuración de IVA es requerida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const dataToSend = {
      razon_social: formData.razon_social.trim(),
      horas: parseFloat(formData.horas),
      monto_base: parseFloat(formData.monto_base),
      tipo: parseInt(formData.tipo),
      iva: parseInt(formData.iva)
    };

    // Si estamos editando, incluir el ID
    if (client?.id) {
      dataToSend.id = client.id;
    }

    onSave(dataToSend);
  };

  const handleChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const isEditing = !!client;

  // Helper para obtener el label de una opción
  const getOptionLabel = (options, value) => {
    const option = options.find(opt => opt.value === parseInt(value));
    return option ? option.label : '';
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {isEditing ? '✏️ Editar Cliente' : '➕ Agregar Nuevo Cliente'}
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Razón Social - Ocupa toda la fila */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Razón Social"
                value={formData.razon_social}
                onChange={handleChange('razon_social')}
                error={!!errors.razon_social}
                helperText={errors.razon_social}
                disabled={loading}
                required
                InputProps={{
                  startAdornment: <Business sx={{ mr: 1, color: 'action.active' }} />
                }}
                placeholder="Ej: Empresa ABC S.A."
              />
            </Grid>
            
            {/* Primera fila de campos */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Cantidad de Horas"
                value={formData.horas}
                onChange={handleChange('horas')}
                error={!!errors.horas}
                helperText={errors.horas}
                disabled={loading}
                required
                inputProps={{ min: 0, step: 0.5 }}
                InputProps={{
                  startAdornment: <AccessTime sx={{ mr: 1, color: 'action.active' }} />
                }}
                placeholder="Ej: 160"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Monto Base"
                value={formData.monto_base}
                onChange={handleChange('monto_base')}
                error={!!errors.monto_base}
                helperText={errors.monto_base}
                disabled={loading}
                required
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: <AttachMoney sx={{ mr: 1, color: 'action.active' }} />
                }}
                placeholder="Ej: 100000"
              />
            </Grid>

            {/* Segunda fila - Selects */}
            <Grid item xs={12} sm={6}>
              <FormControl 
                fullWidth 
                error={!!errors.tipo}
                disabled={loading}
                required
              >
                <InputLabel>Tipo de Cliente</InputLabel>
                <Select
                  value={formData.tipo}
                  onChange={handleChange('tipo')}
                  label="Tipo de Cliente"
                  startAdornment={<Category sx={{ mr: 1, color: 'action.active' }} />}
                >
                  <MenuItem value="">
                    <em>Seleccione un tipo</em>
                  </MenuItem>
                  {tipoOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.tipo && (
                  <FormHelperText>{errors.tipo}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl 
                fullWidth 
                error={!!errors.iva}
                disabled={loading}
                required
              >
                <InputLabel>Configuración IVA</InputLabel>
                <Select
                  value={formData.iva}
                  onChange={handleChange('iva')}
                  label="Configuración IVA"
                  startAdornment={<Receipt sx={{ mr: 1, color: 'action.active' }} />}
                >
                  <MenuItem value="">
                    <em>Seleccione configuración</em>
                  </MenuItem>
                  {ivaOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.iva && (
                  <FormHelperText>{errors.iva}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>

          {/* Mostrar información adicional en modo edición */}
          {isEditing && formData.tipo && formData.iva && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Alert severity="info" sx={{ mb: 1 }}>
                <strong>Información actual:</strong>
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <strong>Tipo:</strong> {getOptionLabel(tipoOptions, formData.tipo)}
                </Grid>
                <Grid item xs={6}>
                  <strong>IVA:</strong> {getOptionLabel(ivaOptions, formData.iva)}
                </Grid>
              </Grid>
            </Box>
          )}

          {Object.keys(errors).length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Por favor corrija los errores antes de continuar
            </Alert>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={onClose} 
            disabled={loading}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button 
            type="submit"
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear Cliente')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ClientModal;